/**
 * «Мой доступ» — `GET /api/v1/access/me` (E1.5, #1336; UI by #1645).
 *
 * What the caller may see in the selected organization: `legacy` (no grant —
 * the role decides, as before E1.5) or `managed` (a grant — its capabilities
 * within its scope). The screens a subject is offered are decided from this
 * answer (`lib/route-permissions.ts`, `decideScreen`), not from the role alone.
 */
import {
  accessDepartmentsResponseSchema,
  accessGrantsListResponseSchema,
  accessLogResponseSchema,
  accessMeResponseSchema,
  assignAccessGrantResponseSchema,
  memberAccessResponseSchema,
  revokeAccessGrantResponseSchema,
  type AccessDepartmentsResponse,
  type AccessGrantAssignment,
  type AccessGrantsListResponse,
  type AccessLogCategory,
  type AccessLogResponse,
  type AccessMeResponse,
  type AssignAccessGrantResponse,
  type MemberAccessResponse,
  type RevokeAccessGrantResponse,
} from '@aica/api-types/schemas';
import { BaseApiClient } from '../base';
import { ACCESS_GRANT_REFUSALS } from '../access-refusals';
import { API_ENDPOINTS } from '../endpoints';

export class AccessApi extends BaseApiClient {
  /** Validated: an answer of another shape is a failure, never a guess. */
  async getMe(): Promise<AccessMeResponse> {
    return this.validatedRequest(API_ENDPOINTS.ACCESS_ME, accessMeResponseSchema);
  }

  /**
   * The active grants of the org's current members — one read for the «Доступ»
   * column of «Настройки → Команда» (E1.5 PR 5, #1336). An unreadable grant is
   * listed, as `state: 'unreadable'`.
   */
  async getGrants(): Promise<AccessGrantsListResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.ACCESS_GRANTS,
      accessGrantsListResponseSchema
    );
  }

  /**
   * The org's departments (id, name, parent) for the access forms' picker and
   * the «Доступ» column — a read of its own, so that its failure never takes
   * the grants list or a member's access with it. Only for a caller who may
   * manage access (403 otherwise).
   */
  async getDepartments(): Promise<AccessDepartmentsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.ACCESS_DEPARTMENTS,
      accessDepartmentsResponseSchema
    );
  }

  /**
   * One page of the org's access log (#2492), newest first. `cursor` is the
   * previous page's `nextCursor`. Only for a caller who sees the team list
   * (403 otherwise).
   */
  async getEvents(params: {
    category?: AccessLogCategory;
    cursor?: string;
  }): Promise<AccessLogResponse> {
    const search = new URLSearchParams();
    if (params.category) search.set('category', params.category);
    if (params.cursor) search.set('cursor', params.cursor);
    const qs = search.toString();
    return this.validatedRequest(
      qs ? `${API_ENDPOINTS.ACCESS_EVENTS}?${qs}` : API_ENDPOINTS.ACCESS_EVENTS,
      accessLogResponseSchema
    );
  }

  /** One member's grant and the departments they head (the form's suggestions). */
  async getMemberAccess(userId: string): Promise<MemberAccessResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.USERS_ACCESS(userId),
      memberAccessResponseSchema
    );
  }

  /**
   * Give (or replace) a member's grant. The body is the assignment as the
   * server's schema reads it — the capability list is never sent, the server
   * snapshots the preset's. Gated by `enableAccessGrants`: 403
   * `tier_not_eligible` / `feature_disabled` (the catalog note of
   * `access.analytics_grants`).
   */
  async assignGrant(
    userId: string,
    assignment: AccessGrantAssignment
  ): Promise<AssignAccessGrantResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.USERS_ACCESS(userId),
      assignAccessGrantResponseSchema,
      {
        method: 'PUT',
        body: JSON.stringify(assignment),
        expectedRefusals: ACCESS_GRANT_REFUSALS,
      }
    );
  }

  /** Revoke the member's grant; `revoked: false` when there was none. */
  async revokeGrant(userId: string): Promise<RevokeAccessGrantResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.USERS_ACCESS(userId),
      revokeAccessGrantResponseSchema,
      { method: 'DELETE', expectedRefusals: ACCESS_GRANT_REFUSALS }
    );
  }
}
